"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { accent } from "@/lib/palette";
import UserMenu from "./UserMenu";

interface NavigationProps {
  user?: { email: string; username: string | null } | null;
}

const logo = (
  <svg viewBox="0 0 32 32" width="40" height="40" aria-hidden="true">
    <g fill="none" stroke="#8b7ab8" strokeWidth="1.2" strokeLinecap="round">
      <path d="M16 16 Q 20 14, 24 10 Q 28 6, 28 4" />
      <path d="M16 16 Q 12 18, 8 22 Q 4 26, 4 28" />
    </g>
    <g fill="none" stroke="#8b7ab8" strokeWidth="0.8" strokeLinecap="round" opacity="0.6">
      <path d="M16 16 Q 18 12, 22 8 Q 26 4, 30 2" />
      <path d="M16 16 Q 14 20, 10 24 Q 6 28, 2 30" />
    </g>
    <circle cx="6" cy="8" r="1" fill="#c4b5fd" />
    <circle cx="26" cy="24" r="1" fill="#c4b5fd" />
    <circle cx="10" cy="5" r="0.8" fill="#c4b5fd" opacity="0.7" />
    <circle cx="22" cy="27" r="0.8" fill="#c4b5fd" opacity="0.7" />
    <circle cx="4" cy="18" r="0.6" fill="#c4b5fd" opacity="0.5" />
    <circle cx="28" cy="14" r="0.6" fill="#c4b5fd" opacity="0.5" />
    <circle cx="16" cy="16" r="4" fill={accent.DEFAULT} opacity="0.15" />
    <circle cx="16" cy="16" r="2.5" fill={accent.DEFAULT} opacity="0.25" />
    <circle cx="16" cy="16" r="1.5" fill="#f5f3ff" />
  </svg>
);

export default function Navigation({ user }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`
        fixed top-0 left-0 right-0 z-50 px-4 py-3 transition-all duration-300
        ${scrolled
          ? "bg-bg-primary/90 backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
        }
      `}
    >
      <div className="max-w-[1200px] mx-auto flex items-center justify-between gap-4">
        <Link
          href="/"
          className="flex items-center gap-3 text-text-primary font-bold text-2xl no-underline hover:no-underline"
        >
          {logo}
          <span>Qosm</span>
        </Link>

        <div className="flex items-center gap-3">
          <Link
            href="/docs/getting-started"
            className="text-sm font-medium no-underline hover:no-underline transition-colors"
            style={{ color: "var(--color-text-muted)" }}
          >
            Docs
          </Link>
          <Link
            href="/blog"
            className="text-sm font-medium no-underline hover:no-underline transition-colors"
            style={{ color: "var(--color-text-muted)" }}
          >
            Blog
          </Link>
          {user ? (
            <UserMenu displayName={user.username ?? user.email} />
          ) : (
            <>
              <span className="text-xs font-semibold uppercase tracking-wider text-text-muted ml-2">Private beta</span>
              <Link
                href="/workspace/invite"
                className="text-sm font-semibold px-5 py-2 rounded-lg no-underline hover:no-underline transition-colors"
                style={{ backgroundColor: accent.DEFAULT, color: accent.onAccent }}
              >
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
